import dayjs, { Dayjs } from 'dayjs'

import { ShortestLeaveIntervalEnum } from '../../types/leave-policy'
import { ITimeOption } from './types'
import { safeToISOString } from '../../functions/safe-date-format'

interface IGenerateTimeOptionsParamsEnd {
  type: 'end'
  shortestLeaveInterval: ShortestLeaveIntervalEnum
  startTimeValue: Dayjs
  hourFormatString: string
  minuteStep: number
  isHalfDayHoliday: boolean
  workingHoursInDay: number
  today?: Dayjs
}

interface IGenerateTimeOptionsParamsStart {
  type: 'start'
  shortestLeaveInterval: ShortestLeaveIntervalEnum
  hourFormatString: string
  minuteStep: number
  workingHoursInDay?: number
  today?: Dayjs
}

const MINUTES_IN_DAY = 24 * 60

// Minimum minutes a leave must occupy for a given interval. FULL-DAY is intentionally
// excluded - full-day leaves use the calendar range picker, not the time picker.
export function getMinIntervalMinutes(interval: ShortestLeaveIntervalEnum, workingHoursInDay: number): number | null {
  switch (interval) {
    case ShortestLeaveIntervalEnum.halfDay:
      return Math.round((workingHoursInDay * 60) / 2)
    case ShortestLeaveIntervalEnum.oneHour:
      return 60
    case ShortestLeaveIntervalEnum.halfHour:
      return 30
    case ShortestLeaveIntervalEnum.fifteenMinutes:
      return 15
    default:
      return null
  }
}

export const generateTimeOptions = (params: IGenerateTimeOptionsParamsStart | IGenerateTimeOptionsParamsEnd): ITimeOption[] => {
  const options: ITimeOption[] = []
  const current = params.today || dayjs()
  let minuteStep = params.minuteStep
  let startAt = 0
  if (params.type === 'end') {
    if (params.shortestLeaveInterval === ShortestLeaveIntervalEnum.halfDay) {
      const hasFractionalHours = params.workingHoursInDay % 1 !== 0
      minuteStep = hasFractionalHours ? 15 : 60
      startAt = params.startTimeValue.minute()
    }
    if (params.shortestLeaveInterval === ShortestLeaveIntervalEnum.oneHour) {
      minuteStep = 60
      startAt = params.startTimeValue.minute()
    }
    if (params.shortestLeaveInterval === ShortestLeaveIntervalEnum.halfHour) {
      minuteStep = 30
      const startMinute = params.startTimeValue.minute()
      startAt = startMinute - 30 < 0 ? startMinute : startMinute - 30
    }
    if (params.shortestLeaveInterval === ShortestLeaveIntervalEnum.fifteenMinutes) {
      minuteStep = 15
      startAt = 0
    }
  }

  const startMinInterval = params.type === 'start' ? getMinIntervalMinutes(params.shortestLeaveInterval, params.workingHoursInDay ?? 0) : null

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = startAt; minute < 60; minute += minuteStep) {
      const time = current.hour(hour).minute(minute).second(0).millisecond(0)
      const isoString = safeToISOString(time)
      let disabled = false
      if (params.type === 'end') {
        disabled = isDisabledTimeCore({
          startTime: params.startTimeValue,
          selectedTime: time,
          shortestLeaveInterval: params.shortestLeaveInterval,
          isHalfDayHoliday: params.isHalfDayHoliday,
          workingHoursInDay: params.workingHoursInDay,
        })
      } else if (startMinInterval !== null) {
        // Prevents a start time that can't fit its minimum interval before midnight.
        // Uses strict > so a start where start + minInterval lands exactly at midnight
        // (e.g. 20:00 + 4h half-day on an 8h workday = 24:00) is still allowed - end
        // picker exposes 24:00 as a valid last-of-day option below.
        disabled = hour * 60 + minute + startMinInterval > MINUTES_IN_DAY
      }
      options.push({
        value: isoString,
        label: time.format(params.hourFormatString),
        disabled,
        key: isoString,
      })
    }
  }

  if (params.type === 'end') {
    // Append 24:00 (= next-day 00:00) so users can end a leave exactly at midnight
    // without spilling into the next day. The value stored is a real next-day dayjs
    // instant; the label reads "24:00" in 24h format to disambiguate from today's 00:00,
    // and "12:00 AM" in 12h format (matches Ant Design/system convention).
    const midnight = current.add(1, 'day').hour(0).minute(0).second(0).millisecond(0)
    const midnightLabel = params.hourFormatString.includes('A') ? '12:00 AM' : '24:00'
    options.push({
      value: safeToISOString(midnight),
      label: midnightLabel,
      disabled: isDisabledTimeCore({
        startTime: params.startTimeValue,
        selectedTime: midnight,
        shortestLeaveInterval: params.shortestLeaveInterval,
        isHalfDayHoliday: params.isHalfDayHoliday,
        workingHoursInDay: params.workingHoursInDay,
      }),
      key: safeToISOString(midnight),
    })
  }

  return options
}

interface IIsDisabledTimeCoreParams {
  startTime: Dayjs
  selectedTime: Dayjs
  shortestLeaveInterval: ShortestLeaveIntervalEnum
  isHalfDayHoliday: boolean
  workingHoursInDay: number
}

function isDisabledTimeCore({ startTime, selectedTime, shortestLeaveInterval, isHalfDayHoliday, workingHoursInDay }: IIsDisabledTimeCoreParams): boolean {
  // A start built from "now" can carry stray seconds/ms; minute diffs truncate, so the
  // %30/%15 rules would come out one minute short and disable every option.
  const start = startTime.second(0).millisecond(0)
  const totalMinutes = Math.round(workingHoursInDay * 60)
  const halfDayMinutes = Math.round(totalMinutes / 2)

  if (!selectedTime.isAfter(start)) {
    return true
  }

  if (selectedTime.isAfter(start.add(totalMinutes, 'minute'))) {
    return true
  }

  if (isHalfDayHoliday && selectedTime.isAfter(start.add(halfDayMinutes, 'minute'))) {
    return true
  }

  if (shortestLeaveInterval === ShortestLeaveIntervalEnum.fullDay) {
    const fullDayEnd = start.add(totalMinutes, 'minute')
    return !selectedTime.isSame(fullDayEnd)
  }

  if (shortestLeaveInterval === ShortestLeaveIntervalEnum.halfDay) {
    const fullDayEnd = start.add(totalMinutes, 'minute')
    const halfDayEnd = start.add(halfDayMinutes, 'minute')
    return !selectedTime.isSame(fullDayEnd) && !selectedTime.isSame(halfDayEnd)
  }

  if (shortestLeaveInterval === ShortestLeaveIntervalEnum.oneHour) {
    return start.minute() !== selectedTime.minute()
  }

  if (shortestLeaveInterval === ShortestLeaveIntervalEnum.halfHour) {
    return selectedTime.diff(start, 'minute') % 30 !== 0
  }

  if (shortestLeaveInterval === ShortestLeaveIntervalEnum.fifteenMinutes) {
    return selectedTime.diff(start, 'minute') % 15 !== 0
  }

  return false
}
